import { Component, effect, ElementRef, EventEmitter, HostListener, Output, ViewChild, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdminService } from 'src/app/shared/services/admin-panel/admin.service';
import { HelpersService } from 'src/app/shared/services/helpers/helpers.service';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import * as _ from 'lodash';
import {
    ADMIN_SETTINGS_CONFIG_PROJECT,
    ADMIN_SETTINGS_CONFIG_ACCOUNT,
    ADMIN_SETTINGS_CONFIG_DEFAULT_ADMIN,
    ADMIN_SETTINGS_CATEGORY_ORDER_PROJECT,
    ADMIN_SETTINGS_CATEGORY_ORDER_ACCOUNT,
    ADMIN_SETTINGS_CATEGORY_ORDER_DEFAULT_ADMIN,
    ADMIN_PERMISSION_TYPE_OPTIONS,
    ADMIN_HIERARCHY_OPTIONS,
    getCategoryKeyByItemKey,
    getItemDescriptionByKey,
    AdminSettingsConfig
} from 'src/app/shared/constants/admin-settings.config';

declare var Helpers: any;

@Component({
    selector: 'app-admin-panel',
    templateUrl: './admin-panel.component.html',
    styleUrls: ['./admin-panel.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule]
})
export class AdminPanelComponent implements OnInit {
    /** Emitted after admin rules are created or updated so parent can refresh menu gates. */
    @Output() rulesChanged = new EventEmitter<void>();

    expandedSettings: { [ruleId: string]: boolean } = {};
    expandedUsers: { [ruleId: string]: boolean } = {};
    brandDetails: any = {};
    accountDetails: any = {};
    isAccessDenied: boolean = false;
    constructor(
        private adminService: AdminService,
        private helpersService: HelpersService,
        private appStateService: AppStateService
    ) {
        // Initialize configuration based on context
        this.initializeConfiguration();

        effect(() => {
            if (this.appStateService.initialized()) {
                const isDefaultAdmin = this.helpersService.helpers['isDefaultAdmin'];
                
                // For account and project level, check ADMIN_PERMISSION_LIST permission
                if (!isDefaultAdmin && !this.hasAdminPermissionListAccess()) {
                    console.warn('Access denied: Admin panel requires ADMIN_PERMISSION_LIST permission');
                    this.isAccessDenied = true;
                    this.isLoadingRules = false;
                    return;
                }
                
                if (isDefaultAdmin) {
                    // For default_admin, fetch rules directly
                    this.isLoadingRules = true;
                    this.fetchRules();
                } else if (this.helpersService.helpers['isAccount']) {
                    const account: any = this.appStateService.accountDetails()
                    if (!_.isEmpty(account)) {
                        this.accountDetails = account;
                        this.isLoadingRules = true;
                        this.fetchRules();
                    }
                } else {
                    const brandDetail: any = this.appStateService.brandDetails();
                    if (!_.isEmpty(brandDetail)) {
                        this.brandDetails = brandDetail;
                        this.isLoadingRules = true;
                        this.fetchRules();
                    }
                }
            }
        });
        
        // Also check for default_admin on init (in case effect doesn't trigger)
        const isDefaultAdmin = this.helpersService.helpers['isDefaultAdmin'];
        
        // For account and project level, check ADMIN_PERMISSION_LIST permission
        if (!isDefaultAdmin && !this.hasAdminPermissionListAccess()) {
            console.warn('Access denied: Admin panel requires ADMIN_PERMISSION_LIST permission');
            this.isAccessDenied = true;
            this.isLoadingRules = false;
        } else if (isDefaultAdmin) {
            this.isLoadingRules = true;
            this.fetchRules();
        }
    }

    ngOnInit(): void {
        // Check permission on init as well
        const isDefaultAdmin = this.helpersService.helpers['isDefaultAdmin'];
        
        if (!isDefaultAdmin && !this.hasAdminPermissionListAccess()) {
            console.warn('Access denied: Admin panel requires ADMIN_PERMISSION_LIST permission');
            this.isAccessDenied = true;
            this.isLoadingRules = false;
        }
    }

    /**
     * Check if user has ADMIN_PERMISSION_LIST permission from adminRulesDic
     * @returns true if user has read or write access to ADMIN_PERMISSION_LIST, false otherwise
     */
    private hasAdminPermissionListAccess(): boolean {
        const adminRulesDic = _.get(Helpers, 'adminRulesDic', {});
        const adminPermissionList = adminRulesDic['ADMIN_PERMISSION_LIST'];
        
        // Check if permission exists and has read or write access
        if (adminPermissionList && (adminPermissionList.read === true || adminPermissionList.write === true)) {
            return true;
        }
        
        return false;
    }

    /**
     * Check if user has WRITE permission for ADMIN_PERMISSION_LIST
     * Public method for template usage
     * @returns true if user has write access to ADMIN_PERMISSION_LIST, false otherwise
     */
    hasAdminPermissionListWriteAccess(): boolean {
        const isDefaultAdmin = this.helpersService.helpers['isDefaultAdmin'];
        
        // Default admin always has write access
        if (isDefaultAdmin) {
            return true;
        }
        
        const adminRulesDic = _.get(Helpers, 'adminRulesDic', {});
        const adminPermissionList = adminRulesDic['ADMIN_PERMISSION_LIST'];
        
        // Check if permission exists and has write access
        if (adminPermissionList && adminPermissionList.write === true) {
            return true;
        }
        
        return false;
    }

    /**
     * Initialize configuration based on current context (account, project, or default_admin)
     */
    private initializeConfiguration(): void {
        const isDefaultAdmin = this.helpersService.helpers['isDefaultAdmin'];
        const isAccount = this.helpersService.helpers['isAccount'];

        if (isDefaultAdmin) {
            this.settings = ADMIN_SETTINGS_CONFIG_DEFAULT_ADMIN;
            this.categoryOrder = ADMIN_SETTINGS_CATEGORY_ORDER_DEFAULT_ADMIN;
        } else if (isAccount) {
            this.settings = ADMIN_SETTINGS_CONFIG_ACCOUNT;
            this.categoryOrder = ADMIN_SETTINGS_CATEGORY_ORDER_ACCOUNT;
        } else {
            this.settings = ADMIN_SETTINGS_CONFIG_PROJECT;
            this.categoryOrder = ADMIN_SETTINGS_CATEGORY_ORDER_PROJECT;
        }
    }

    fetchRules(page: number = 1): void {
        var unique_id: string = '';
        const isDefaultAdmin = this.helpersService.helpers['isDefaultAdmin'];
        
        if (isDefaultAdmin) {
            unique_id = '586cf0042e6a153e539de334';
        } else if (this.helpersService.helpers['isAccount'] && !_.isEmpty(this.accountDetails)) {
            unique_id = this.accountDetails._id;
        } else {
            if (!_.isEmpty(this.brandDetails)) {
                unique_id = this.brandDetails._id;
            }
        }
        this.isLoadingRules = true;
        this.currentPage = page;

        this.adminService.getRules(unique_id, page, this.pageSize).subscribe({
            next: (response) => {
                console.log('Rules fetched successfully:', response);
                // Update the rules array with fetched data
                this.rules = response.data.rules || [];
                // Note: Pagination metadata might be in response.data or response itself
                this.totalRecords = response.data.total_records || response.total_records || 0;
                this.totalPages = response.data.total_pages || response.total_pages || 0;
                this.isLoadingRules = false;
            },
            error: (error) => {
                console.error('Error fetching rules:', error);
                this.createRuleError = 'Failed to fetch rules. Please try again.';
                this.isLoadingRules = false;

                // Show error notification
                Helpers.smallAlertBox('Error', 'Failed to load rules. Please try again.', 'error', 3000);
            }
        });
    }


    isSidePanelOpen: boolean = false;
    isDropdownOpen: boolean = false;
    isPermissionDropdownOpen: boolean = false;
    isHierarchyDropdownOpen: boolean = false;
    ruleTitle: string = '';

    // API required fields - rules_applied_on will be determined dynamically from this.helpersService.helpers['isAccount']

    // Order-controlled keys for rendering sections
    categoryOrder: string[] = [];

    // Settings data used to render grouped checkbox options
    settings: AdminSettingsConfig = {};

    // Selection state: category -> item -> boolean
    selectedItems: { [category: string]: { [item: string]: boolean } } = {};
    addedItems: string[] = [];
    showNoSelectionMessage: boolean = false;
    permissionTypeOptions: string[] = ADMIN_PERMISSION_TYPE_OPTIONS;
    selectedPermissionType: string | null = null;
    hierarchyOptions: string[] = ADMIN_HIERARCHY_OPTIONS;
    selectedHierarchyOption: string | null = null;
    customEmails: string[] = [];
    customEmailInput: string = '';
    customEmailError: string | null = null;

    // Created rules list to show on main page - matches API response structure
    rules: Array<{
        _id: string;
        unique_id: string;
        account_id: string;
        rules_applied_on: string;
        created_date: string;
        title: string;
        permission_type: string;
        permission_list: string[];
        user_type_for_permission: string;
        created_by: {
            manager_id: string;
            email_address: string;
            name: string;
        };
        last_updated_by: {
            manager_id: string;
            email_address: string;
            name: string;
            updated_date: string;
        };
        update_log: any[];
        brand_id: string;
        custom_list?: string[];
    }> = [];

    // Form errors for validation feedback
    formErrors: string[] = [];

    // Loading and error states
    isCreatingRule: boolean = false;
    createRuleError: string | null = null;

    // Pagination properties
    currentPage: number = 1;
    pageSize: number = 10;
    totalPages: number = 0;
    totalRecords: number = 0;
    isLoadingRules: boolean = true; // Start as true to show loader initially

    // View toggle properties
    viewMode: 'table' | 'card' = 'table';

    // Template references for outside-click detection
    @ViewChild('settingsDropdownRef', { static: false }) settingsDropdownRef?: ElementRef<HTMLElement>;
    @ViewChild('permissionDropdownRef', { static: false }) permissionDropdownRef?: ElementRef<HTMLElement>;
    @ViewChild('hierarchyDropdownRef', { static: false }) hierarchyDropdownRef?: ElementRef<HTMLElement>;

    isEditMode: boolean = false;
    currentEditIndex: number | null = null;
    originalRuleData: any = null; // Store original rule data for comparison
    maxRulesLimit: number = 10;
    maxCustomEmailsLimit: number = 20;
    maxRuleNameLength: number = 50;

    openSidePanel(): void {
        // Check write permission for creating/editing rules
        if (!this.hasAdminPermissionListWriteAccess()) {
            Helpers.smallAlertBox('Access Denied', 'You do not have write permission to create or edit rules. Only read access is available.', 'warning', 4000);
            return;
        }
        
        // Check if rule limit is reached
        if (this.rules.length >= this.maxRulesLimit) {
            Helpers.smallAlertBox('Limit Reached', `Maximum ${this.maxRulesLimit} rules allowed. Please edit an existing rule to update it.`, 'warning', 4000);
            return;
        }
        this.resetForm();
        this.isSidePanelOpen = true;
    }

    canCreateNewRule(): boolean {
        // Check both write permission and rule limit
        return this.hasAdminPermissionListWriteAccess() && this.rules.length < this.maxRulesLimit;
    }

    getDisabledButtonTooltip(): string {
        if (!this.hasAdminPermissionListWriteAccess()) {
            return 'You do not have write permission to create rules. Only read access is available.';
        }
        if (this.rules.length >= this.maxRulesLimit) {
            return `Maximum ${this.maxRulesLimit} rules allowed. Please edit an existing rule to update it.`;
        }
        return '';
    }

    closeSidePanel(): void {
        this.isSidePanelOpen = false;
        this.isDropdownOpen = false;
    }

    toggleDropdown(): void {
        this.isDropdownOpen = !this.isDropdownOpen;
        if (this.isDropdownOpen) {
            this.isPermissionDropdownOpen = false;
            this.isHierarchyDropdownOpen = false;
        }
    }

    togglePermissionDropdown(): void {
        this.isPermissionDropdownOpen = !this.isPermissionDropdownOpen;
        if (this.isPermissionDropdownOpen) {
            this.isDropdownOpen = false;
            this.isHierarchyDropdownOpen = false;
        }
    }

    toggleHierarchyDropdown(): void {
        this.isHierarchyDropdownOpen = !this.isHierarchyDropdownOpen;
        if (this.isHierarchyDropdownOpen) {
            this.isDropdownOpen = false;
            this.isPermissionDropdownOpen = false;
        }
    }

    onItemChange(categoryKey: string, itemKey: string, checked: boolean): void {
        if (!this.selectedItems[categoryKey]) {
            this.selectedItems[categoryKey] = {};
        }
        this.selectedItems[categoryKey][itemKey] = checked;

        // Immediately update addedItems when checkbox changes
        this.updateAddedItems();
    }

    getTotalSelectedCount(): number {
        let count = 0;
        for (const category of Object.keys(this.selectedItems)) {
            count += Object.values(this.selectedItems[category]).filter(Boolean).length;
        }
        return count;
    }

    updateAddedItems(): void {
        // Clear existing addedItems
        this.addedItems = [];

        // Collect all selected items
        for (const categoryKey of Object.keys(this.selectedItems)) {
            const selections = this.selectedItems[categoryKey] || {};
            for (const [itemKey, isChecked] of Object.entries(selections)) {
                if (isChecked) {
                    this.addedItems.push(itemKey); // Store the key for API
                }
            }
        }

        // Hide any warning message since we have selections
        this.showNoSelectionMessage = false;
    }

    isItemSelected(categoryKey: string, itemKey: string): boolean {
        return this.selectedItems[categoryKey] && this.selectedItems[categoryKey][itemKey] === true;
    }


    selectPermissionType(option: string): void {
        this.selectedPermissionType = option;
        this.isPermissionDropdownOpen = false;
    }


    selectHierarchyOption(option: string): void {
        this.selectedHierarchyOption = option;
        this.isHierarchyDropdownOpen = false;
        // reset email list if switching away from Custom
        if (option !== 'Custom') {
            this.customEmails = [];
            this.customEmailInput = '';
            this.customEmailError = null;
        }
    }

    private isValidEmail(email: string): boolean {
        // basic email validation; can be tightened later
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
        return re.test(email);
    }

    addCustomEmails(): void {
        if (this.selectedHierarchyOption !== 'Custom') return;
        this.customEmailError = null;
        const parts = (this.customEmailInput || '').split(/[\s,;]+/).map(p => p.trim()).filter(p => !!p);
        if (parts.length === 0) {
            this.customEmailError = 'Enter at least one email address.';
            return;
        }

        // Check if adding these emails would exceed the limit
        const existing = new Set(this.customEmails.map(e => e.toLowerCase()));
        const validNewEmails: string[] = [];
        const invalid: string[] = [];

        for (const email of parts) {
            if (!this.isValidEmail(email)) {
                invalid.push(email);
                continue;
            }
            const key = email.toLowerCase();
            if (!existing.has(key)) {
                validNewEmails.push(email);
            }
        }

        // Check if adding valid emails would exceed the limit
        if (this.customEmails.length + validNewEmails.length > this.maxCustomEmailsLimit) {
            const remainingSlots = this.maxCustomEmailsLimit - this.customEmails.length;
            if (remainingSlots > 0) {
                this.customEmailError = `Maximum ${this.maxCustomEmailsLimit} email addresses allowed. You can add ${remainingSlots} more.`;
            } else {
                this.customEmailError = `Maximum ${this.maxCustomEmailsLimit} email addresses allowed. Please remove some emails before adding new ones.`;
            }
            // Add only the emails that fit within the limit
            const emailsToAdd = validNewEmails.slice(0, remainingSlots);
            for (const email of emailsToAdd) {
                this.customEmails.push(email);
                existing.add(email.toLowerCase());
            }
        } else {
            // Add all valid emails
            for (const email of validNewEmails) {
                this.customEmails.push(email);
                existing.add(email.toLowerCase());
            }
        }

        if (invalid.length > 0 && this.customEmails.length + validNewEmails.length <= this.maxCustomEmailsLimit) {
            this.customEmailError = `Invalid: ${invalid.join(', ')}`;
        }
        this.customEmailInput = '';
    }

    removeCustomEmail(email: string): void {
        this.customEmails = this.customEmails.filter(e => e !== email);
        // Clear error if we're now under the limit
        if (this.customEmails.length < this.maxCustomEmailsLimit && this.customEmailError?.includes('Maximum')) {
            this.customEmailError = null;
        }
    }

    onCustomEmailKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            this.addCustomEmails();
        }
    }

    private isFormValid(): boolean {
        this.formErrors = [];
        if (!this.ruleTitle.trim()) {
            this.formErrors.push('Title is required.');
        } else if (this.ruleTitle.trim().length > this.maxRuleNameLength) {
            this.formErrors.push(`Title cannot exceed ${this.maxRuleNameLength} characters.`);
        }
        if (this.addedItems.length === 0) {
            this.formErrors.push('Select at least one setting from the first dropdown.');
        }
        if (!this.selectedPermissionType) {
            this.formErrors.push('Select a permission type.');
        }
        if (!this.selectedHierarchyOption) {
            this.formErrors.push('Select a hierarchy access option.');
        } else if (this.selectedHierarchyOption === 'Custom') {
            if (this.customEmails.length === 0) {
                this.formErrors.push('Add at least one email for Custom hierarchy access.');
            } else if (this.customEmails.length > this.maxCustomEmailsLimit) {
                this.formErrors.push(`Maximum ${this.maxCustomEmailsLimit} custom email addresses allowed.`);
            }
        }
        return this.formErrors.length === 0;
    }

    onCreateRule(): void {
        console.log('Create Rule button clicked!');

        // Check write permission for creating/updating rules
        if (!this.hasAdminPermissionListWriteAccess()) {
            Helpers.smallAlertBox('Access Denied', 'You do not have write permission to create or update rules. Only read access is available.', 'warning', 4000);
            return;
        }

        // Check if rule limit is reached (only for new rules, not edits)
        if (!this.isEditMode && this.rules.length >= this.maxRulesLimit) {
            Helpers.smallAlertBox('Limit Reached', `Maximum ${this.maxRulesLimit} rules allowed. Please use an existing rule to update an existing one.`, 'warning', 4000);
            return;
        }

        // Clear any previous errors
        this.formErrors = [];
        this.createRuleError = null;

        // Validate form
        if (!this.isFormValid()) {
            console.log('Form validation failed:', this.formErrors);
            return;
        }

        // Get account_id and brand_id from appStateService
        var account_id = '';
        var brand_id = '';
        const isDefaultAdmin = this.helpersService.helpers['isDefaultAdmin'];
        
        if (isDefaultAdmin) {
            account_id = '586cf0042e6a153e539de334';
        } else if (this.helpersService.helpers['isAccount']) {
            account_id = this.accountDetails._id;
        } else {
            account_id = this.brandDetails.account_id;
            brand_id = this.brandDetails._id;
        }
        
        // Determine rules_applied_on based on context
        const rules_applied_on: 'account' | 'project' | 'default_admin' = 
            isDefaultAdmin ? 'default_admin' : 
            (this.helpersService.helpers['isAccount'] ? 'account' : 'project');

        // Validate required API fields
        if (!account_id) {
            this.formErrors.push('Account ID is required.');
            return;
        }

        console.log('Creating rule with data:', {
            title: this.ruleTitle,
            selectedItems: this.addedItems,
            permissionType: this.selectedPermissionType,
            hierarchyOption: this.selectedHierarchyOption,
            customEmails: this.customEmails
        });

        this.isCreatingRule = true;

        // Map form data to API parameters
        const ruleData = {
            account_id: account_id,
            brand_id: brand_id,
            rules_applied_on: rules_applied_on,
            title: this.ruleTitle.trim(),
            permission_type: this.selectedPermissionType as 'READ' | 'WRITE',
            permission_list: this.addedItems,
            user_type_for_permission: this.mapHierarchyOptionToAPI(this.selectedHierarchyOption as string),
            custom_list: this.selectedHierarchyOption === 'Custom' ? this.customEmails : undefined
        };
        console.log('Rule data:', ruleData);
        // Determine if this is an update or create operation
        if (this.isEditMode && this.currentEditIndex !== null) {
            // Check if data has changed
            if (!this.hasRuleDataChanged(rules_applied_on)) {
                // No changes detected, close panel without API call
                Helpers.smallAlertBox('Info', 'No changes detected. Rule remains unchanged.', 'info', 3000);
                this.isCreatingRule = false;
                this.resetForm();
                this.closeSidePanel();
                return;
            }

            // Update existing rule - create update data without account_id and brand_id
            const ruleToUpdate = this.rules[this.currentEditIndex];
            const updateData = {
                rules_applied_on: rules_applied_on,
                title: this.ruleTitle.trim(),
                permission_type: this.selectedPermissionType as 'READ' | 'WRITE',
                permission_list: this.addedItems,
                user_type_for_permission: this.mapHierarchyOptionToAPI(this.selectedHierarchyOption as string),
                custom_list: this.selectedHierarchyOption === 'Custom' ? this.customEmails : undefined
            };
            this.adminService.updateRule(ruleToUpdate._id, updateData).subscribe({
                next: (response) => {
                    console.log('Rule updated successfully:', response);

                    // Show success notification
                    Helpers.smallAlertBox('Success', 'Rule updated successfully', 'success', 3000);

                    // Refresh the rules list from the server
                    this.fetchRules();
                    this.rulesChanged.emit();

                    this.isCreatingRule = false;
                    this.resetForm();
                    this.closeSidePanel();
                },
                error: (error) => {
                    console.error('Error updating rule:', error);
                    this.createRuleError = 'Failed to update rule. Please try again.';
                    this.isCreatingRule = false;

                    // Show error notification
                    Helpers.smallAlertBox('Error', 'Failed to update rule. Please try again.', 'error', 3000);
                }
            });
        } else {
            // Create new rule
            this.adminService.createRule(ruleData).subscribe({
                next: (response) => {
                    console.log('Rule created successfully:', response);

                    // Show success notification
                    Helpers.smallAlertBox('Success', 'Rule created successfully', 'success', 3000);

                    // Refresh the rules list from the server
                    this.fetchRules();
                    this.rulesChanged.emit();

                    this.isCreatingRule = false;
                    this.resetForm();
                    this.closeSidePanel();
                },
                error: (error) => {
                    console.error('Error creating rule:', error);
                    this.createRuleError = 'Failed to create rule. Please try again.';
                    this.isCreatingRule = false;

                    // Show error notification
                    Helpers.smallAlertBox('Error', 'Failed to create rule. Please try again.', 'error', 3000);
                }
            });
        }
    }

    private mapHierarchyOptionToAPI(option: string): 'HIERARCHY_LEVEL' | 'HIERARCHY_VALUE' | 'CUSTOM' {
        switch (option) {
            case 'Hierarchy Level Access':
                return 'HIERARCHY_LEVEL';
            case 'Hierarchy Value Access':
                return 'HIERARCHY_VALUE';
            case 'Custom':
                return 'CUSTOM';
            default:
                return 'HIERARCHY_LEVEL';
        }
    }

    /**
     * Check if the current form data differs from the original rule data
     * @param rules_applied_on - The current rules_applied_on value
     * @returns true if data has changed, false otherwise
     */
    private hasRuleDataChanged(rules_applied_on: 'account' | 'project' | 'default_admin'): boolean {
        if (!this.originalRuleData) {
            return true; // If no original data, consider it changed (shouldn't happen in edit mode)
        }

        // Compare title
        if (this.ruleTitle.trim() !== this.originalRuleData.title) {
            return true;
        }

        // Compare permission_type
        if (this.selectedPermissionType !== this.originalRuleData.permission_type) {
            return true;
        }

        // Compare user_type_for_permission
        const currentUserType = this.mapHierarchyOptionToAPI(this.selectedHierarchyOption as string);
        if (currentUserType !== this.originalRuleData.user_type_for_permission) {
            return true;
        }

        // Compare rules_applied_on
        if (rules_applied_on !== this.originalRuleData.rules_applied_on) {
            return true;
        }

        // Compare permission_list (arrays)
        const originalPermissionList = this.originalRuleData.permission_list || [];
        const currentPermissionList = this.addedItems || [];
        if (originalPermissionList.length !== currentPermissionList.length) {
            return true;
        }
        const sortedOriginal = [...originalPermissionList].sort();
        const sortedCurrent = [...currentPermissionList].sort();
        if (JSON.stringify(sortedOriginal) !== JSON.stringify(sortedCurrent)) {
            return true;
        }

        // Compare custom_list (arrays, handling undefined/null)
        const originalCustomList = this.originalRuleData.custom_list || [];
        const currentCustomList = this.selectedHierarchyOption === 'Custom' ? (this.customEmails || []) : [];
        
        // If both are empty, they're the same
        if (originalCustomList.length === 0 && currentCustomList.length === 0) {
            return false;
        }
        
        // Compare arrays
        if (originalCustomList.length !== currentCustomList.length) {
            return true;
        }
        const sortedOriginalCustom = [...originalCustomList].sort();
        const sortedCurrentCustom = [...currentCustomList].sort();
        if (JSON.stringify(sortedOriginalCustom) !== JSON.stringify(sortedCurrentCustom)) {
            return true;
        }

        // No changes detected
        return false;
    }

    onEditRule(rule: any): void {
        // Check write permission for editing rules
        if (!this.hasAdminPermissionListWriteAccess()) {
            Helpers.smallAlertBox('Access Denied', 'You do not have write permission to edit rules. Only read access is available.', 'warning', 4000);
            return;
        }

        const idx = this.rules.indexOf(rule);
        if (idx === -1) return;

        // Set edit mode and store the rule index
        this.isEditMode = true;
        this.currentEditIndex = idx;

        // Store original rule data for comparison
        this.originalRuleData = {
            title: rule.title,
            permission_type: rule.permission_type,
            permission_list: rule.permission_list ? [...rule.permission_list] : [],
            user_type_for_permission: rule.user_type_for_permission,
            custom_list: rule.custom_list ? [...rule.custom_list] : [],
            rules_applied_on: rule.rules_applied_on
        };

        // Determine context from rule's rules_applied_on field (for older rules compatibility)
        let context: 'project' | 'account' | 'default_admin' = 'project';
        if (rule.rules_applied_on === 'default_admin') {
            context = 'default_admin';
        } else if (rule.rules_applied_on === 'account') {
            context = 'account';
        } else {
            // Fallback to helpers if rules_applied_on is not set (for older rules)
            const isDefaultAdmin = this.helpersService.helpers['isDefaultAdmin'];
            const isAccount = this.helpersService.helpers['isAccount'];
            if (isDefaultAdmin) {
                context = 'default_admin';
            } else if (isAccount) {
                context = 'account';
            }
        }

        // Reinitialize configuration based on rule's context (important for older rules)
        this.initializeConfigurationForContext(context);

        // Populate form with existing rule data
        this.ruleTitle = rule.title;
        this.addedItems = [...rule.permission_list];
        this.selectedPermissionType = rule.permission_type;
        this.selectedHierarchyOption = this.mapAPIHierarchyToUI(rule.user_type_for_permission);
        this.customEmails = rule.custom_list ? [...rule.custom_list] : [];

        // Rebuild selectedItems for settings using the correct context
        this.selectedItems = {};
        for (const itemKey of this.addedItems) {
            const categoryKey = getCategoryKeyByItemKey(itemKey, context);
            if (categoryKey) {
                if (!this.selectedItems[categoryKey]) this.selectedItems[categoryKey] = {};
                this.selectedItems[categoryKey][itemKey] = true;
            }
        }

        // Open the side panel for editing
        this.isSidePanelOpen = true;
    }

    /**
     * Initialize configuration for a specific context (used when editing rules)
     */
    private initializeConfigurationForContext(context: 'project' | 'account' | 'default_admin'): void {
        if (context === 'default_admin') {
            this.settings = ADMIN_SETTINGS_CONFIG_DEFAULT_ADMIN;
            this.categoryOrder = ADMIN_SETTINGS_CATEGORY_ORDER_DEFAULT_ADMIN;
        } else if (context === 'account') {
            this.settings = ADMIN_SETTINGS_CONFIG_ACCOUNT;
            this.categoryOrder = ADMIN_SETTINGS_CATEGORY_ORDER_ACCOUNT;
        } else {
            this.settings = ADMIN_SETTINGS_CONFIG_PROJECT;
            this.categoryOrder = ADMIN_SETTINGS_CATEGORY_ORDER_PROJECT;
        }
    }

    private mapAPIHierarchyToUI(apiValue: string): string {
        switch (apiValue) {
            case 'HIERARCHY_LEVEL':
                return 'Hierarchy Level Access';
            case 'HIERARCHY_VALUE':
                return 'Hierarchy Value Access';
            case 'CUSTOM':
                return 'Custom';
            default:
                return 'Hierarchy Level Access';
        }
    }

    getHierarchyDisplayText(apiValue: string): string {
        return this.mapAPIHierarchyToUI(apiValue);
    }

    getItemDescriptionByKey(itemKey: string, rule?: any): string | null {
        // Determine context from rule's rules_applied_on field if provided (for displaying rules)
        let context: 'project' | 'account' | 'default_admin' = 'project';
        
        if (rule && rule.rules_applied_on) {
            if (rule.rules_applied_on === 'default_admin') {
                context = 'default_admin';
            } else if (rule.rules_applied_on === 'account') {
                context = 'account';
            } else {
                context = 'project';
            }
        } else {
            // Fallback to helpers if rule is not provided (for form display)
            const isDefaultAdmin = this.helpersService.helpers['isDefaultAdmin'];
            const isAccount = this.helpersService.helpers['isAccount'];
            if (isDefaultAdmin) {
                context = 'default_admin';
            } else if (isAccount) {
                context = 'account';
            }
        }
        
        return getItemDescriptionByKey(itemKey, context);
    }

    toggleSettingsExpansion(ruleId: string): void {
        this.expandedSettings[ruleId] = !this.expandedSettings[ruleId];
    }

    isSettingsExpanded(ruleId: string): boolean {
        return this.expandedSettings[ruleId] || false;
    }

    toggleUsersExpansion(ruleId: string): void {
        this.expandedUsers[ruleId] = !this.expandedUsers[ruleId];
    }

    isUsersExpanded(ruleId: string): boolean {
        return this.expandedUsers[ruleId] || false;
    }

    // View toggle methods
    setViewMode(mode: 'table' | 'card'): void {
        this.viewMode = mode;
    }

    isTableView(): boolean {
        return this.viewMode === 'table';
    }

    isCardView(): boolean {
        return this.viewMode === 'card';
    }


    private resetForm(): void {
        this.ruleTitle = '';
        this.addedItems = [];
        this.selectedItems = {};
        this.selectedPermissionType = null;
        this.selectedHierarchyOption = null;
        this.customEmails = [];
        this.customEmailInput = '';
        this.customEmailError = null;
        this.formErrors = [];
        this.showNoSelectionMessage = false;
        this.isEditMode = false;
        this.currentEditIndex = null;
        this.originalRuleData = null;
        this.createRuleError = null;
        this.isCreatingRule = false;
    }

    // Helper methods for template error checking
    hasTitleError(): boolean {
        return this.formErrors.some(e => e.includes('Title'));
    }

    hasSettingError(): boolean {
        return this.formErrors.some(e => e.includes('setting'));
    }

    hasPermissionError(): boolean {
        return this.formErrors.some(e => e.includes('permission'));
    }

    hasHierarchyError(): boolean {
        return this.formErrors.some(e => e.includes('hierarchy'));
    }

    // Method to check if form is valid for button state
    isFormValidForSubmission(): boolean {
        return this.ruleTitle.trim() !== '' &&
            this.ruleTitle.trim().length <= this.maxRuleNameLength &&
            this.addedItems.length > 0 &&
            this.selectedPermissionType !== null &&
            this.selectedHierarchyOption !== null &&
            (this.selectedHierarchyOption !== 'Custom' || (this.customEmails.length > 0 && this.customEmails.length <= this.maxCustomEmailsLimit));
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (!this.isSidePanelOpen) return;
        const insideSettings = this.settingsDropdownRef?.nativeElement.contains(target) ?? false;
        const insidePermission = this.permissionDropdownRef?.nativeElement.contains(target) ?? false;
        const insideHierarchy = this.hierarchyDropdownRef?.nativeElement.contains(target) ?? false;

        if (!insideSettings) this.isDropdownOpen = false;
        if (!insidePermission) this.isPermissionDropdownOpen = false;
        if (!insideHierarchy) this.isHierarchyDropdownOpen = false;
    }
}
